// assistantWidth.js — THE DOCKED SIDEBAR'S WIDTH, remembered per person
// (AI_ASSISTANT_SPEC v1 §8 "Sidebar … adjustable width"; Nika's pick 7 A;
// DESIGN-WL-ASSISTANT-MAIN-VIEW-1, lane current-23-assistant).
//
// `root.assistantWidth` (px, null = the default 640) is the drawer's width —
// the shell publishes it to `--assistant-width` for the drawer AND the content
// column it reserves (app.js). The drag and the keyboard (Arrow keys step,
// Home / End jump to the bounds) change it LIVE; the width is REMEMBERED when
// the gesture ends: localStorage paints first (`symbols_assistant_width`), the
// `user_preferences` row (`assistantWidth`) follows the person to every
// device, the record wins at boot and a local width with no record migrates
// up. Writes follow the home-prefs durability law (single-flight on the shared
// prefs queue, the payload composed from the live row at send time, an
// unloaded row read first, the newest decision last).
//
// Bounds: 320 px (the drawer's narrowest readable column) to half the
// viewport — the same clamp the drag always enforced.
//
// Frank note: el.call serializes each registered export on its own (FA204),
// so every export here is self-contained — the bounds are restated.

// A width inside the bounds for a viewport `vw` (px), or null for "the default".
export const clampAssistantWidth = (value, vw) => {
  const n = Math.round(Number(value))
  if (!Number.isFinite(n) || n <= 0) return null
  const view = Number(vw) > 0 ? Number(vw) : 1200
  const max = Math.max(320, Math.floor(view * 0.5))
  return Math.max(320, Math.min(max, n))
}

// The width a key asks for, from the current one — or null when the key is not
// a resize key. The handle sits on the drawer's INNER edge, so the arrow that
// points INTO the content widens: ArrowLeft on a right dock, ArrowRight on a
// left dock. Shift steps five times as far.
export const assistantWidthForKey = (current, key, opts) => {
  const o = opts || {}
  const view = Number(o.vw) > 0 ? Number(o.vw) : 1200
  const max = Math.max(320, Math.floor(view * 0.5))
  const now = Number(current) > 0 ? Number(current) : 640
  const step = o.shift ? 120 : 24
  const widen = o.dock === 'left' ? 'ArrowRight' : 'ArrowLeft'
  const narrow = o.dock === 'left' ? 'ArrowLeft' : 'ArrowRight'
  let next = null
  if (key === widen) next = now + step
  else if (key === narrow) next = now - step
  else if (key === 'Home') next = 320
  else if (key === 'End') next = max
  if (next === null) return null
  return Math.max(320, Math.min(max, Math.round(next)))
}

// The stored width on this device, or null when the person never chose.
export const readStoredAssistantWidth = (storage) => {
  try {
    const store = storage || (typeof localStorage !== 'undefined' ? localStorage : null)
    const v = Number(store ? store.getItem('symbols_assistant_width') : null)
    return Number.isFinite(v) && v >= 320 ? Math.round(v) : null
  } catch (_) {
    return null
  }
}

// Boot reconcile, once the user_preferences row has landed. PURE:
//   { apply: px }    the record holds a width that differs from the state
//   { migrate: px }  no record width; this device holds one — persist it up
//   null             nothing to do
export const reconcileAssistantWidthFromPrefs = (current, prefs, stored) => {
  const norm = (v) => {
    const n = Math.round(Number(v))
    return Number.isFinite(n) && n >= 320 ? n : null
  }
  const recorded = norm(prefs && typeof prefs === 'object' ? prefs.assistantWidth : null)
  if (recorded) return recorded !== norm(current) ? { apply: recorded } : null
  const local = norm(stored)
  return local ? { migrate: local } : null
}

// setAssistantWidth(px) via el.call — or setAssistantWidth(root, px, sdk) from
// shell code: clamp to the viewport, write the state and this device's copy,
// then the person's record under the durability law. Returns the write task.
export function setAssistantWidth(a, b, c) {
  const viaCall = typeof a === 'number' || a == null
  const raw = viaCall ? a : b
  const self = this && typeof this === 'object' ? this : null
  const root = viaCall ? (self && self.state && self.state.root) || null : a
  const sdk = viaCall
    ? (self && (self.sdk || (self.context && self.context.sdk))) || null
    : c || null
  if (!root || typeof root.update !== 'function') return null
  const vw = typeof window !== 'undefined' && window.innerWidth ? window.innerWidth : 1200
  const n = Math.round(Number(raw))
  if (!Number.isFinite(n) || n <= 0) return null
  const value = Math.max(320, Math.min(Math.max(320, Math.floor(vw * 0.5)), n))
  const prefs = root.userPrefs && typeof root.userPrefs === 'object' ? root.userPrefs : null
  const patch = { assistantWidth: value }
  if (prefs && prefs.assistantWidth !== value) patch.userPrefs = { ...prefs, assistantWidth: value }
  root.update(patch, { preventFetch: true })
  try {
    if (typeof localStorage !== 'undefined')
      localStorage.setItem('symbols_assistant_width', String(value))
  } catch (err) {
    console.warn('[shell] assistant-width cache write failed:', err && err.message)
  }
  if (!sdk || typeof sdk.execute !== 'function') return null
  const w = root.workspace
  const key =
    String(
      (w && (w.id || w._id)) || (root.jwtClaims && root.jwtClaims.active_workspace_id) || ''
    ) || 'user'
  if (!root.__navbarSaveQueue) root.__navbarSaveQueue = new Map()
  const queue = root.__navbarSaveQueue
  root.__assistantWidthSeq = (root.__assistantWidthSeq || 0) + 1
  const seq = root.__assistantWidthSeq
  const prior = queue.get(key) || Promise.resolve()
  const task = prior
    .catch(() => {})
    .then(async () => {
      if (seq !== root.__assistantWidthSeq) return null
      // PER-PATH PATCH — names only `assistantWidth` (CORE-USERPREFS-PATCH-PER-PATH-1).
      const row = await sdk.execute('workspaceProject.userPreferences', 'patch', {
        patch: { set: { assistantWidth: value } }
      })
      if (seq === root.__assistantWidthSeq) {
        const now = root.userPrefs && typeof root.userPrefs === 'object' ? root.userPrefs : null
        root.update(
          {
            userPrefs: now
              ? { ...now, assistantWidth: value }
              : row && typeof row === 'object'
                ? row
                : { assistantWidth: value }
          },
          { preventFetch: true }
        )
      }
      return row
    })
  queue.set(key, task)
  return task.catch((err) => {
    console.warn('[shell] user_preferences assistantWidth save failed:', err && err.message)
    return null
  })
}
